import {
	REPORTED_EXCEPTION,
	SearchFormModel,
	SearchModel,
	webapi,
	DateUtils,
	NumberUtils,
	ModuleDefinition as Module,
	DefinitionUtils,
	ButtonDefinition as Button,
	FormDefinition as Form,
	isEmpty,
	getSrchTokens,
	Windows,
} from 'ps-gui';
import removeAccents from 'remove-accents';
import { getPhrase } from '../data/phrases';

import Immutable from 'immutable';
import { getSessionToken } from '../data/session';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);


/** @module utils */

export function downloadFile(fileId, fileName, type) {
	const contentType = 'application/octet-stream';
	fileName = encodeRFC5987ValueChars(fileName);
	document.getElementById('downloadFrame').src = webapi.getDecoratedUrl('/api/file/' + fileId) + '?fileName=' + encodeURIComponent(fileName) + '&type=' + encodeURIComponent(type);
}


/**
 * Prevadi seznam hodnot primitivnich typu na mnozinu techto hodnot
 * @param {Immutable.List|Array} list seznam primitivnich typu
 * @returns {Object} vraci objekt, jehoz klici jsou polozky ze vstupniho seznamu a hodnotami true - jedna se tedy o mnozinu tvorenou polozkami z vstupniho seznamu
 */
export function listToSet(list) {
	let res = {};
	if (list) {
		list.forEach(item => {
			res[item] = true;
		});
	}
	return res;
}

/**
 * Encoduje vstupni retezec podle RFC5987. Pouziva se k encodovani UTF-8 nazvu souboru pro pouziti v hlavicce content-disposition
 * @param {String} str vstupni retezec
 * @returns vraci vstupni retezec encodovany podle RFC5987
 */
function encodeRFC5987ValueChars(str) {
	return (
		encodeURIComponent(str)
			// Note that although RFC3986 reserves "!", RFC5987 does not,
			// so we do not need to escape it
			.replace(/['()*]/g, c => `%${c.charCodeAt(0).toString(16)}`) // i.e., %27 %28 %29 %2a (Note that valid encoding of "*" is %2A
			// which necessitates calling toUpperCase() to properly encode)
			// The following are not required for percent-encoding per RFC5987,
			// so we can allow for a little better readability over the wire: |`^
			.replace(/%(7C|60|5E)/g, (str, hex) =>
				String.fromCharCode(parseInt(hex, 16))
			)
	);
}

export async function exportToCsv(model, options) {
	options = options || {};
	let filter = model.getFullSearchFilter(true);
	const url = model.getUri();
	filter.firstRow = 1;
	filter.maxRowsPerPage = 99 * 1000 * 1000;
	const resultModel = model.getResultModel();
	if (resultModel) {
		filter._columnTextMap = Object.assign({}, resultModel.getColumnTextMap(), options.columnTextMap);
	}
	if (options.outputColumns) {
		filter._output = options.outputColumns;
	}
	if (options.url) {
		filter._url = options.url;
	}
	if (filter._url) {
		filter._url += '/csv';
	}
	let res = await webapi.execute(url + '/csv', filter);
	if (res !== REPORTED_EXCEPTION) {
		downloadFile(res, 'export.csv');
	}
}

export function createSearchModel(url) {
	return new SearchModel().setUri(url)
		.setResultSetModuleId('PortalResultSet')
		.setMaxRowsPerPage(16)
		.setCsvExportCallback(exportToCsv)
		.setForm(new SearchFormModel());
}

export function createSimpleSearchFormDefinition(formItems, entityName) {
	return DefinitionUtils.setLabels(
		Form().setItems(
			formItems.length > 0 ? formItems.concat(Button('search', getPhrase('search'))) : []
		).setHorizontal(true).setActionMode('all'),
		entityName
	);
}

export async function exportToExcel(packageName, pars, fileName) {
	const res = await webapi.execute('/api/export/excel/' + packageName, pars);
	if (res === REPORTED_EXCEPTION) {
		return;
	}
	downloadFile(res, fileName || 'export.xlsx');
}

export function getSearchDefinition(path, uri, formItems, entityName, pathPrefix = ['tmp'], noDefaultRowAction, onIconMouseDown) {
	const formDefinition = createSimpleSearchFormDefinition(formItems, entityName);
	const fullPath = pathPrefix.concat(path);
	const model = new SearchModel().setForm(
		new SearchFormModel().setDefinition(formDefinition)
	).setUri(uri)
		.setCsvExportCallback(exportToCsv)
		.setEntityName(entityName)
		.setResultSetModuleId('PortalResultSet')
		.setOnIconMouseDown(onIconMouseDown)
		.setMaxRowsPerPage(32)
		.setFixedProfile(Array.isArray(path) ? path.join('_') : path)
		.setDefaultRowAction(noDefaultRowAction ? null : 'entityLink');
	return Module(fullPath, model);
}


/*
 * parsuje retezec ve formatu partyId/roleTypeId
 * do objektu {partyId, roleTypeId}
 */
export function parseRoleId(roleId) {
	if (!roleId) {
		return null;
	}
	let idx = roleId.indexOf('/');
	return {
		partyId: parseInt(roleId.substring(0, idx)),
		roleTypeId: roleId.substring(idx + 1)
	};
}

const twoDecFmt = NumberUtils.getFixedDecimalFormatNumber(2);
export function formatFileSize(bytes) {
	if (bytes >= 1024 * 1024) {
		return twoDecFmt(bytes / 1024.0 / 1024.0) + ' MB';
	} else if (bytes >= 1024) {
		return twoDecFmt(bytes / 1024.0) + ' kB';
	} else {
		return bytes + ' B';
	}
}

export function stripDia(value) {
	if (!value) {
		return value;
	}
	return removeAccents(value);
}

export function getSqlFulltextFilter(value, autoWildCard = false) {
	if (value) {
		// normalize dates
		value = value.replace(/([0-9]{1,2})[.,/][\s]*([0-9]{1,2})[.,/][\s]*([0-9]{4})/g, '$1.$2.$3');
		// - = *
		value = value.replace(/-/g, '*');
	}
	value = stripDia(value);
	let tokens = [].concat.apply([], (value || '').split('"').map((v, i) => {
		return i % 2 ? '"' + v + '"' : v.split(/[^\w*.]+/g);
	})).filter(Boolean);
	tokens = tokens.map(token => {
		const date = DateUtils.parseDate(token);
		const astIdx = token.indexOf('*');
		if (astIdx === token.length - 1) {
			return '"' + token + '"';
		} else if (!DateUtils.isInvalidDate(date)) {
			return 'D' + DateUtils.toRawDate(date);
		} else {
			return token;
		}
	}).filter(token => !!token);
	if (autoWildCard && value && value.endsWith(tokens[tokens.length - 1])) {
		tokens[tokens.length - 1] += '%';
	}
	return tokens.map(token => '"' + token + '"').join(' and ').replace(/\*/g, '%');
}

const htmlFormatCharMap = {
	'<': '&lt;',
	'>': '&gt;',
	'*': {
		tag: 'b'
	}
};

/**
 * Escapes html tags. Formats string with markup in type of *xxx* to become
 * html tag <>xxx</>. It helps content to be used with DOM.
 * !!! Is very sensitive to value given. Replaces also every random <|> in
 * text, where it should not be html tag. For markup replaces every * in text
 * for current b tag. Could cause possible errors.
 * !! Every <|> character escapes to &lt;|&gt; but for markup and puts <|> as not escaped character.
 * @export
 * @param {String} value input content
 * @return {String} content with escaped HTML and markuped values for DOM
 */
export function formatHtml(value) {
	if (typeof value !== 'string') {
		return null;
	}
	let openTags = {};
	let res = [];
	let strtIdx = 0;
	const len = value.length;
	for (let i = 0; i < len; i++) {
		let c = value.charAt(i);
		let replaceWith = htmlFormatCharMap[c];
		if (replaceWith) {
			if (i > strtIdx) {
				res.push(value.substring(strtIdx, i));
			}
			strtIdx = i + 1;
			if (typeof replaceWith === 'string') {
				res.push(replaceWith);
			} else {
				const tag = replaceWith.tag;
				if (openTags[tag]) {
					res.push('</');
					res.push(tag);
					res.push('>');
					openTags[tag] = false;
				} else {
					res.push('<');
					res.push(tag);
					res.push('>');
					openTags[tag] = true;
				}
			}
		} else if (c === '[') {
			if (value.charAt(i + 1) === '[') {
				i += 2;
				let endIdx = value.indexOf(']]', i);
				if (endIdx > i) {
					if (strtIdx < i - 2) {
						res.push(value.substring(strtIdx, i - 2));
					}
					let link = value.substring(i, endIdx);
					let href, text;
					let titleIdx = link.lastIndexOf('|');
					if (titleIdx < 0) {
						href = link;
						text = link;
					} else {
						href = link.substring(0, titleIdx);
						text = link.substring(titleIdx + 1);
					}
					res.push('<a href="');
					res.push(escapeHtml(href));
					res.push('" target="_blank">');
					res.push(escapeHtml(text));
					res.push('</a>');
					i = endIdx + 1;
					strtIdx = i + 1;
				}
			}
		}
	}
	if (strtIdx < value.length) {
		res.push(value.substring(strtIdx));
	}
	return res.join('');
}

function escapeHtml(value) {
	if (typeof value === 'string') {
		return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/, '&gt;');
	} else {
		return value;
	}
}

/**
 * Formats phone number given from database in format +xxxyyyzzzbbb. Other formats are not present in database.
 * !!! Sensitive to value given. Made to format czech syntax given from database.
 * !! If value|string is too short, still puts empty space behind.
 * @export
 * @param {String} value given number
 * @return {String} formatted number in format +xxx yyy zzz bbb
 */
export function formatPhoneNumber(value) {
	if (!value || typeof value !== 'string') {
		return '';
	}
	return value.substring(0, 4) + ' ' + value.substring(4, 7) + ' ' + value.substring(7, 10) + ' ' + value.substring(10);
}

/**
 * @typedef {Object} PasswordValidationResult
 * @property {boolean} valid je heslo validni?
 * @property {boolean} min8Chars obsahuje heslo alespon 8 znaku?
 * @property {boolean} upperLower obsahuje alespon jeden upper a jeden lowercase znak
 * @property {boolean} specialChars obsahuje alespon jeden nealpha znak
 */

/**
 * Provede validaci hesla
 * @param {String} pwd heslo k validaci
 * @returns {PasswordValidationResult}
 */
export function validatePassword(pwd) {
	let res = {};
	if (!pwd) {
		return res;
	}
	const dialess = stripDia(pwd);
	res.min8Chars = pwd.length >= 8;
	res.upperLower = dialess.toLowerCase() !== dialess && dialess.toUpperCase() !== dialess;
	res.specialChars = /[^a-zA-Z]/.test(dialess);
	res.valid = res.min8Chars && res.upperLower && res.specialChars;
	return res;
}

export function validateSharePct(value) {
	return (value < 0 || value > 100) ? getPhrase('shareMustBeBetween0And100') : null;
}

export function validateRuntime(value) {
	return value < 0 ? getPhrase('runtimeMustNotBeNegative') : null;
}



/**
 * Provede normalizaci tel. cisla:
 * 	Odstrani neciselne znaky.
 * 	Pokud se jedna o 9 znake cislo bez predvolby doplni '+420'.
 * 	Jinak doplni/ponecha '+'.
 * @param {String} value telefonni cislo
 * @returns {String} normalizovane telefonni cislo
 */
export function normalizePhoneNumber(value) {
	if (!value || typeof value !== 'string') {
		return '';
	}
	const isComplete = value.startsWith('+');
	value = value.replace(/[^0-9]/g, '');
	if (value.length === 9 && !isComplete) {
		return '+420' + value;
	} else {
		return '+' + value;
	}
}

export function getGlobalContextChangeChecker(module) {
	return async function checkGlobalContextChange(prevProps) {
		const prevGlobalContext = prevProps ? prevProps.globalContext : null;
		if (prevGlobalContext === module.props.globalContext) {
			return;
		}
		const model = module.getModel();
		if (model.checkGlobalContextChange) {
			const newModel = await model.checkGlobalContextChange(module.props.globalContext);
			if (newModel !== model) {
				module.setModel(newModel);
			}
		}
	};
}

export function wait(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}


export function processModifications(orig, modif) {
	if (!Immutable.isMap(modif)) {
		return modif;
	}
	orig = orig || EMPTY_MAP;
	if (orig !== modif) {
		modif = modif.set('mod$', true);
	}
	let modKeys = [];
	let modValues = [];
	modif.entrySeq().forEach(e => {
		const key = e[0];
		if (key.startsWith('mod$')) {
			return;
		}
		let value = e[1];
		if (Immutable.isMap(value)) {
			const origValue = orig.get(key) || EMPTY_MAP;
			if (value !== origValue) {
				modKeys.push(key);
				modValues.push({key, value: processModifications(origValue, value)});
			}
		} else if (Immutable.isList(value)) {
			const origValue = orig.get(key) || EMPTY_LIST;
			if (value !== origValue) {
				modKeys.push(key);
				value = value.map((value, index) => {
					return processModifications(origValue.get(index), value);
				});
				modValues.push({
					key,
					value
				});
			}
		} else {
			if (value !== orig.get(key)) {
				modKeys.push(key);
			}
		}
	});
	modKeys.forEach(key => {
		modif = modif.set('mod$' + key, true);
	});
	modValues.forEach(entry => {
		//console.log('setting ', entry.key, ' = ', (Immutable.isMap(entry.value) ? entry.value.toJS() : entry.value));
		modif = modif.set(entry.key, entry.value);
	});
	return modif;
}


export function validateISRC(value, mandatory = false) {
	if (!value) {
		if (mandatory) {
			return getPhrase('missingISRC');
		} else {
			return null;
		}
	}
	if (/^[A-Z]{2}[A-Z0-9]{3}[0-9]{7}$/.test(value)) {
		return null;
	} else {
		return getPhrase('invalidISRC');
	}
}

export function validatePinDob(value) {
	if (parsePinDob(value).type === 'error') {
		return getPhrase('invalidPinDob');
	} else {
		return null;
	}
}

export function parsePinDob(value) {
	if (typeof value === 'string') {
		value = value.trim();
	}
	if (!value) {
		return {
			type: 'empty'
		};
	}
	if ((value.indexOf(',') > 0) || (value.indexOf('.') > 0)) {
		let dateValue = DateUtils.parseDate(value);
		if (!DateUtils.isInvalidDate(dateValue)) {
			return {
				type: 'date',
				value: dateValue
			};
		} else {
			return {
				type: 'error'
			};
		}
	} else {
		if (validatePin(value)) {
			return {
				type: 'error'
			};
		} else {
			return {
				type: 'pin',
				value
			};
		}
	}
}


export function validatePin(value) {
	if (isEmpty(value)) {
		return null;
	}
	if (/^[0-9]{2}[01235678]{1}[0-9]{1}[0-3]{1}[0-9]{1}[0-9]{3}[0-9]{0,1}$/.test(value)) {
		let modOk = false;
		if (value.length >= 10) {
			const kontrola = parseInt(value.substring(9, 10));
			if (!isNaN(kontrola)) {
				let modulo = parseInt(value.substring(0, 9)) % 11;
				if (modulo === 10) {
					modulo = 0;
				}
				if (modulo === kontrola) {
					modOk = true;
				}
			}
		} else {
			modOk = true;
		}
		if (modOk) {
			if (getDateFromPin(value)) {
				return null;
			}
		}
	}
	return getPhrase('invalidPin');
}

export function isrcInputFilter(value) {
	if (value) {
		value = value.toUpperCase();
		if (value.length > 5) {
			value = value.substring(0, 5).replace(/[^A-Z0-9]/g, '') + value.substring(5).replace(/[^0-9]/g, '');
			if (value.length > 12) {
				value = value.substring(0, 12);
			}
		} else {
			value = value.substring(0, 5).replace(/[^A-Z0-9]/g, '');
		}
	}
	return value;
}

const pinYearThreashold = (new Date().getFullYear()-1) % 100;
console.log('pinYearThreashold', pinYearThreashold);


export function getDateFromPin(value) {
	if (isEmpty(value)) {
		return null;
	}
	value = value.trim();
	let year = parseInt(value.substring(0, 2));
	let month = parseInt(value.substring(2, 4));
	let day = parseInt(value.substring(4, 6));
	if (year <= pinYearThreashold && value.length >= 10) {
		year += 2000;
	} else {
		year += 1900;
	}
	if ((month > 70) && (year > 2003)) {
		month -= 70;
	} else if (month > 50) {
		month -= 50;
	} else if ((month > 20) && (year > 2003)) {
		month -= 20;
	}
	month--;
	let res = new Date(year, month, day);
	if (res.getFullYear() === year && res.getMonth() === month && day === res.getDate()) {
		return res;
	} else {
		return null;
	}
}

export function getRangeValidator(from, to) {
	return function(value) {
		if (isEmpty(value)) {
			return null;
		}
		return value < from || value > to ? getPhrase('rangeError').replace('$from', from).replace('$to', to) : null;
	};
}

export function sum(rows, prop) {
	if (!rows) {
		return;
	}
	let res;
	rows.forEach(row => {
		if (row.get('_deleted')) {
			return;
		}
		const value = row.get(prop);
		if (typeof value === 'number') {
			if (res) {
				res += value;
			} else {
				res = value;
			}
		}
	});
	return res;
}

const SHARE_EPSILON = 0.00001;

export function compareShares(val1, val2) {
	if (typeof val1 !== 'number' || typeof val2 !== 'number') {
		return null;
	}
	if (val1 < (val2 + SHARE_EPSILON) && val1 > (val2 - SHARE_EPSILON)) {
		return 0;
	} else if (val1 > val2) {
		return 1;
	} else {
		return -1;
	}
}

export function tokenizeNsndFilter(value) {
	if (value && typeof value === 'string') {
		return value.split(/[,;\.\s]+/g);
	}
	return null;
}

export function getNsndTokenFilterProcessor(keys) {
	return function(filter) {
		// console.log('filter', filter);
		for (let i=0; i<keys.length; i++) {
			const key = keys[i];
			// console.log(key, filter[key], tokenizeNsndFilter(filter[key]));
			if (filter[key]) {
				filter[key] = tokenizeNsndFilter(filter[key]);
			}
		}
		return filter;
	};
}


export function getRawNsndTokenFilterProcessor(keys) {
	return function(filter) {
		// console.log('filter', filter);
		for (let i=0; i<keys.length; i++) {
			const key = keys[i];
			// console.log(key, filter[key], tokenizeNsndFilter(filter[key]));
			if (filter[key]) {
				filter[key] = getSrchTokens(filter[key], '');
			}
		}
		return filter;
	};
}

export function openEntityDetail(entityName, entityId) {
	Windows.openWindow({
		module: 'EntityDetail',
		token: getSessionToken(),
		entityName,
		entityId
	}, entityName + 'Detail');
}
